/**
 * Cursor pagination helpers shared by list endpoints registered in
 * PAGINATED_ENDPOINTS (infra/api/constants).
 *
 * Contract (the list envelope carries no total count):
 * - Page 1: ObjectMeta.QueryParam = "offset:0 limit:<firstPageSize>"
 * - Page N: REPLACE the cursor — "offset:0 limit:<walkPageSize> page_state:<token>"
 *   where <token> is the FIRST row's ObjectMeta.QueryParam of the previous page.
 * - The offset value is an on-switch only (ignored by the backend) but must stay present.
 * - Termination: the FIRST row of a page has an empty/absent ObjectMeta.QueryParam —
 *   there are no more pages.
 */

/**
 * Builds the ObjectMeta.QueryParam value for one page.
 * @param {{ limit: number, pageState?: string }} param0
 * @returns {string}
 */
export const buildListQueryParam = ({ limit, pageState }) =>
  `offset:0 limit:${limit}` + (pageState ? ` page_state:${pageState}` : '')

/**
 * Reads the cursor for the NEXT page from a fetched page's rows.
 * @param {any[]} rows
 * @returns {string}
 */
export const extractPageCursor = (rows) => rows?.[0]?.ObjectMeta?.QueryParam || ''

/**
 * Walks a cursor-paginated list until the cursor disappears and returns every row.
 * @param {{
 *  fetchPage: (queryParamValue: string) => Promise<any[]>,
 *  firstPageSize: number,
 *  walkPageSize: number,
 *  maxPages?: number,
 *  onPage?: (pageRows: any[], totalSoFar: number) => void
 * }} param0
 * @returns {Promise<any[]>} all rows concatenated
 */
export const cursorWalkAll = async ({ fetchPage, firstPageSize, walkPageSize, maxPages = 200, onPage }) => {
  const all = []
  let pageState = ''
  let pageSize = firstPageSize
  for (let page = 0; page < maxPages; page++) {
    let rows
    try {
      rows = await fetchPage(buildListQueryParam({ limit: pageSize, pageState }))
    } catch (error) {
      // First page failed: nothing collected, surface to the caller's error handler.
      if (page === 0) throw error
      // Mid-walk failure: keep the pages already collected instead of dropping them.
      console.error('[cursorPaginatedGetAll] walk interrupted after', all.length, 'rows:', error)
      break
    }
    if (!rows?.length) break
    all.push(...rows)
    onPage?.(rows, all.length)
    const cursor = extractPageCursor(rows)
    if (!cursor) break
    pageState = cursor
    pageSize = walkPageSize
  }
  return all
}
